/**
 * Kalkulator odchudzania DNŻ („Ile schudnę i kiedy”): czysta logika obliczeń.
 *
 * Zero DOM, zero zależności. Ten sam plik działa w przeglądarce
 * (window.DnnzWeightLossLogic) i w Node (module.exports), więc testy
 * sprawdzają dokładnie ten kod, który liczy na stronie.
 *
 * Wszystkie liczby przychodzą z konfiguracji inc/data/weight-loss.php (w szablonie
 * atrybut data-config, w testach parser tablicy PHP). W tym pliku nie ma
 * żadnej wartości merytorycznej. Źródła: ZRODLA.md, decyzje: DECYZJE.md.
 *
 * Model (DECYZJE D26, decyzja Leszka Racuta z 23.09.2026): symulacja dzień po dniu.
 * Data zależy wyłącznie od masy, celu i tempa. Niedobór na starcie = tempo × masa
 * startowa × kcal_per_kg / 7; z każdym kilogramem spadku maleje o expenditure_per_kg kcal
 * (spadek wydatku z masą, współczynnik masy we wzorze Mifflina). Spadek masy w ciągu dnia
 * = niedobór / kcal_per_kg. Dla porównania ten sam plan z korektą co correction_days dni:
 * niedobór liczony od nowa z bieżącej masy, przyjęty tylko wtedy, gdy przyspiesza.
 */
(function (root, factory) {
	'use strict';
	var api = factory();
	if (typeof module === 'object' && module.exports) {
		module.exports = api;
	}
	if (root) {
		root.DnnzWeightLossLogic = api;
	}
}(typeof window !== 'undefined' ? window : null, function () {
	'use strict';

	var DAY_MS = 86400000;
	var MONTHS = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];

	// --- Liczby i tekst ----------------------------------------------------

	function toNumber(value) {
		if (value === null || value === '' || typeof value === 'undefined') {
			return NaN;
		}
		var parsed = Number(String(value).trim().replace(',', '.'));
		return isFinite(parsed) ? parsed : NaN;
	}

	/**
	 * Ucina śmieci zmiennoprzecinkowe przed zaokrągleniem (jak w nawodnieniu i tkance).
	 */
	function round6(value) {
		return Math.round(value * 1e6) / 1e6;
	}

	function round1(value) {
		return Math.round(round6(value) * 10) / 10;
	}

	function round05(value) {
		return Math.round(round6(value) * 2) / 2;
	}

	/**
	 * Zapis z przecinkiem i jednym miejscem po nim: 74.3 → "74,3".
	 */
	function formatDecimal(value) {
		var tenths = Math.round(value * 10);
		var sign = tenths < 0 ? '-' : '';
		tenths = Math.abs(tenths);
		return sign + Math.floor(tenths / 10) + ',' + (tenths % 10);
	}

	/**
	 * Odmiana rzeczownika po liczebniku: 1 tydzień, 2 tygodnie, 5 tygodni.
	 */
	function plural(n, one, few, many) {
		if (n === 1) {
			return one;
		}
		var d = n % 10;
		var dd = n % 100;
		if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) {
			return few;
		}
		return many;
	}

	/**
	 * Dzień startu jako liczba dni od 1970-01-01 (UTC, bez stref i zmian czasu).
	 * start: { y, m (1–12), d }.
	 */
	function dayNumber(start) {
		return Math.round(Date.UTC(start.y, start.m - 1, start.d) / DAY_MS);
	}

	/**
	 * Data po `days` dniach od startu, po polsku: „14 marca 2027”.
	 */
	function formatDate(start, days) {
		var date = new Date((dayNumber(start) + days) * DAY_MS);
		return date.getUTCDate() + ' ' + MONTHS[date.getUTCMonth()] + ' ' + date.getUTCFullYear();
	}

	/**
	 * Odczyt pola liczbowego: { value, empty, invalid }. Puste pole to osobny
	 * stan, nie błąd. Błąd = tylko tekst, który nie jest liczbą dodatnią (D7).
	 */
	function readField(raw) {
		var text = raw === null || typeof raw === 'undefined' ? '' : String(raw).trim();
		if (text === '') {
			return { value: null, empty: true, invalid: false };
		}
		var value = toNumber(text);
		if (isNaN(value) || value <= 0) {
			return { value: null, empty: false, invalid: true };
		}
		return { value: value, empty: false, invalid: false };
	}

	// --- Wzory ------------------------------------------------------------

	function bmi(w, heightCm) {
		var m = heightCm / 100;
		return w / (m * m);
	}

	/**
	 * Czy masa leży poniżej progu BMI. Porównanie w kilogramach, obie strony ucięte do
	 * 6 miejsc po przecinku: masa równa progowi (np. 47,36 kg przy 160 cm i BMI 18,5)
	 * nie może wypaść poniżej przez błąd zapisu liczb (DECYZJE D24).
	 */
	function belowBmi(w, heightCm, floorBmi) {
		var m = heightCm / 100;
		return round6(w) < round6(floorBmi * m * m);
	}

	/**
	 * Od 65 lat: najniższa masa w pełnych kg (w górę) przy BMI 23 (DECYZJE D24),
	 * jak próg w kalkulatorze prawidłowej wagi.
	 */
	function seniorMinWeight(heightCm, cfg) {
		var m = heightCm / 100;
		return Math.ceil(round6(cfg.senior.floor_bmi * m * m));
	}

	/**
	 * Najniższa masa w kg (z dokładnością do 0,1 w górę), przy której BMI ≥ progu.
	 */
	function minWeight(heightCm, cfg) {
		var m = heightCm / 100;
		return Math.ceil(round6(cfg.bmi_min * m * m * 10)) / 10;
	}

	/**
	 * Plan od masy base: niedobór na starcie = tempo × masa × kcal_per_kg / 7 (DECYZJE D26).
	 */
	function planFrom(base, rate, cfg) {
		return { base: base, deficit: rate * base * cfg.kcal_per_kg / 7 };
	}

	/**
	 * Niedobór dnia przy masie w: z każdym kilogramem spadku od masy startowej planu
	 * mniejszy o expenditure_per_kg kcal (spadek wydatku z masą; DECYZJE D26).
	 */
	function deficitAt(w, plan, cfg) {
		return plan.deficit - cfg.expenditure_per_kg * (plan.base - w);
	}

	/**
	 * Masa, przy której niedobór spada do zera (plateau planu bez korekty).
	 */
	function plateauWeight(plan, cfg) {
		return plan.base - plan.deficit / cfg.expenditure_per_kg;
	}

	/**
	 * Symulacja dzień po dniu.
	 * opts: { w0, rate, correctionDays (0 = bez korekty), stopAt (masa celu albo null),
	 *         days (maks. liczba dni), marks (masy, dla których zapisujemy dzień) }
	 * Korekta: co correctionDays dni niedobór liczony od nowa z bieżącej masy i tego samego
	 * tempa; przyjmujemy go tylko wtedy, gdy jest większy od dotychczasowego (DECYZJE D26).
	 * Zwraca { reachedDay, weights (masa na koniec każdego dnia, indeks 0 = start),
	 *          deficits (niedobór w każdym dniu), marksDays }.
	 */
	function simulate(opts, cfg) {
		var w = opts.w0;
		var weights = [w];
		var deficits = [];
		var plan = planFrom(w, opts.rate, cfg);
		var marks = opts.marks || [];
		var marksDays = marks.map(function () {
			return null;
		});
		var reachedDay = null;
		for (var d = 1; d <= opts.days; d++) {
			if (opts.correctionDays && d > 1 && (d - 1) % opts.correctionDays === 0) {
				var fresh = planFrom(w, opts.rate, cfg);
				if (deficitAt(w, fresh, cfg) > deficitAt(w, plan, cfg)) {
					plan = fresh;
				}
			}
			var deficit = deficitAt(w, plan, cfg);
			deficits.push(deficit);
			w -= deficit / cfg.kcal_per_kg;
			weights.push(w);
			for (var i = 0; i < marksDays.length; i++) {
				if (marksDays[i] === null && w <= marks[i]) {
					marksDays[i] = d;
				}
			}
			if (opts.stopAt !== null && w <= opts.stopAt) {
				reachedDay = d;
				break;
			}
		}
		return { reachedDay: reachedDay, weights: weights, deficits: deficits, marksDays: marksDays };
	}

	function weeksOf(days) {
		return Math.ceil(days / 7);
	}

	/**
	 * Punkty wykresu: masa na początku każdego tygodnia i w dniu końca symulacji.
	 */
	function curve(weights, floor) {
		var points = [];
		var last = weights.length - 1;
		// Ostatni dzień kończy się tuż pod celem; na wykresie krzywa zatrzymuje się na celu.
		var at = function (d) {
			return typeof floor === 'number' ? Math.max(weights[d], floor) : weights[d];
		};
		for (var d = 0; d <= last; d += 7) {
			points.push({ day: d, weight: at(d) });
		}
		if (last % 7 !== 0) {
			points.push({ day: last, weight: at(last) });
		}
		return points;
	}

	// --- Kiedy osiągnę cel ----------------------------------------------------

	function goalPlan(w0, target, tempo, cfg, start) {
		var rate = cfg.tempos[tempo];
		var horizon = cfg.horizon_weeks * 7;
		var marks = cfg.milestones.map(function (share) {
			return w0 * (1 - share);
		});

		var fixed = simulate({ w0: w0, rate: rate, correctionDays: 0, stopAt: target, days: horizon, marks: marks }, cfg);
		var corrected = simulate({ w0: w0, rate: rate, correctionDays: cfg.correction_days, stopAt: target, days: horizon, marks: [] }, cfg);
		// Bez korekty cel może nie paść w 5 latach z dwóch powodów: masa zatrzymania leży na celu
		// albo nad nim (cel nieosiągalny, linia plateau), albo poniżej celu, tylko droga trwa
		// ponad 5 lat (linia „ponad 5 lat”). DECYZJE D23.
		var stopWeight = fixed.reachedDay === null ? plateauWeight(planFrom(w0, rate, cfg), cfg) : null;
		var unreachable = stopWeight !== null && stopWeight >= target;

		var out = {
			rate: rate,
			startDeficit: planFrom(w0, rate, cfg).deficit,
			reached: fixed.reachedDay !== null,
			days: fixed.reachedDay,
			weeks: fixed.reachedDay === null ? null : weeksOf(fixed.reachedDay),
			date: fixed.reachedDay === null ? null : formatDate(start, fixed.reachedDay),
			// Masa, na której zatrzymuje się chudnięcie bez korekty, do 0,5 kg (DECYZJE D23).
			plateau: unreachable ? round05(stopWeight) : null,
			fixedTooLong: stopWeight !== null && !unreachable,
			correctedReached: corrected.reachedDay !== null,
			correctedDays: corrected.reachedDay,
			correctedWeeks: corrected.reachedDay === null ? null : weeksOf(corrected.reachedDay),
			correctedDate: corrected.reachedDay === null ? null : formatDate(start, corrected.reachedDay),
			milestones: [],
			curve: curve(fixed.weights, target),
			correctedCurve: curve(corrected.weights, target),
			deficits: fixed.deficits,
			correctedDeficits: corrected.deficits
		};

		// Druga data i druga krzywa tylko wtedy, gdy korekta przyspiesza cel o co najmniej
		// second_date_min_days dni (DECYZJE D15). Bez korekty celu nie ma: pokazujemy zawsze.
		out.showCorrected = !out.reached || (out.correctedReached && out.days - out.correctedDays >= cfg.second_date_min_days);

		// Kamienie milowe tylko te, które wypadają co najmniej milestone_min_gap_kg nad celem
		// (bliżej celu dublowałyby jego datę; DECYZJE D16), z datą ze ścieżki bez korekty (brief).
		cfg.milestones.forEach(function (share, i) {
			if (round1(marks[i]) < round1(target + cfg.milestone_min_gap_kg)) {
				return;
			}
			var day = fixed.marksDays[i];
			out.milestones.push({
				share: share,
				weight: round1(marks[i]),
				days: day,
				weeks: day === null ? null : weeksOf(day),
				date: day === null ? null : formatDate(start, day)
			});
		});

		// Wykres bez korekty, gdy celu nie osiąga: rysujemy do dnia celu z korektą.
		if (!out.reached && out.correctedReached) {
			var longer = simulate({ w0: w0, rate: rate, correctionDays: 0, stopAt: null, days: out.correctedDays, marks: [] }, cfg);
			out.curve = curve(longer.weights, target);
		}

		return out;
	}

	// --- Ocena całego wejścia ----------------------------------------------

	/**
	 * input: { age, height, weight, target (teksty pól), tempo (klucz) }
	 * Wiek i wzrost służą wyłącznie blokadom; data zależy od masy, celu i tempa (DECYZJE D26).
	 * start: { y, m, d } dzień, od którego liczymy daty.
	 */
	function evaluate(input, cfg, start) {
		var fields = {
			age: readField(input.age),
			height: readField(input.height),
			weight: readField(input.weight),
			target: readField(input.target)
		};
		var tempo = Object.prototype.hasOwnProperty.call(cfg.tempos, input.tempo) ? input.tempo : null;

		var out = {
			fields: fields,
			blocked: null,
			notice: null,
			hasResult: false,
			minTarget: null,
			goal: null
		};

		// Blokady mają pierwszeństwo przed wynikiem (brief).
		var age = fields.age.value;
		var height = fields.height.value;
		if (age !== null && age < cfg.minor) {
			out.blocked = 'minor';
			return out;
		}
		// Od 65 lat próg BMI 23 dla masy i celu (DECYZJE D24); młodsi: BMI 18,5.
		var senior = age !== null && age >= cfg.senior.age_min;
		var floorBmi = senior ? cfg.senior.floor_bmi : cfg.bmi_min;
		var floorKg = height === null ? null : (senior ? seniorMinWeight(height, cfg) : minWeight(height, cfg));
		if (height !== null && fields.weight.value !== null && belowBmi(fields.weight.value, height, floorBmi)) {
			out.blocked = senior ? 'senior-underweight' : 'underweight';
			out.minTarget = floorKg;
			return out;
		}
		if (height !== null && fields.target.value !== null && belowBmi(fields.target.value, height, floorBmi)) {
			out.blocked = senior ? 'senior-target' : 'target-underweight';
			out.minTarget = floorKg;
			return out;
		}

		var needed = ['age', 'height', 'weight', 'target'];
		for (var i = 0; i < needed.length; i++) {
			if (fields[needed[i]].value === null) {
				return out;
			}
		}
		if (tempo === null) {
			return out;
		}

		var w0 = fields.weight.value;
		if (fields.target.value >= w0) {
			out.notice = 'not-lower';
			return out;
		}
		var g = goalPlan(w0, fields.target.value, tempo, cfg, start);
		out.goal = g;
		if (!g.reached && !g.correctedReached) {
			out.notice = 'too-long';
			return out;
		}
		out.hasResult = true;
		return out;
	}

	/**
	 * Stan pusty: przykłady z konfiguracji, liczba tygodni do celu bez korekty.
	 */
	function samples(cfg) {
		var start = { y: 2026, m: 1, d: 1 }; // daty w tabelce nie są pokazywane
		return cfg.samples.map(function (s) {
			var g = goalPlan(s.weight, s.target, s.tempo, cfg, start);
			return { sample: s, weeks: g.weeks, correctedWeeks: g.correctedWeeks };
		});
	}

	return {
		toNumber: toNumber,
		round1: round1,
		round05: round05,
		formatDecimal: formatDecimal,
		plural: plural,
		formatDate: formatDate,
		readField: readField,
		bmi: bmi,
		belowBmi: belowBmi,
		minWeight: minWeight,
		seniorMinWeight: seniorMinWeight,
		planFrom: planFrom,
		deficitAt: deficitAt,
		plateauWeight: plateauWeight,
		simulate: simulate,
		goalPlan: goalPlan,
		evaluate: evaluate,
		samples: samples
	};
}));
